import { afterSpans, changesIn, diffText, replaceSpans, type Alternative } from '@lyrmo/shared';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Button } from '../../components/Button';
import { cx } from '../../lib/cx';
import { useI18n } from '../../i18n/i18n-context';
import type { Suggest } from './useAlternatives';
import { MarkdownText, type TextExtra, type TextStretch } from './MarkdownText';
import { readableChanges } from './version-changes';
import './explain.css';
import './alts.css';

type AltsState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly options: ReadonlyMap<number, readonly string[]> }
  | { readonly status: 'failed'; readonly message: string };

interface AltsViewProps {
  readonly original: string;
  /** The version as the rewrite returned it, which the changes and their other ways are of. */
  readonly returned: string;
  /** The words swapped in for its changes so far, by the change's index. */
  readonly picks: ReadonlyMap<number, string>;
  readonly suggest: Suggest;
  /** Takes the words now swapped in, and the version as it reads with them. */
  readonly onPick: (picks: ReadonlyMap<number, string>, text: string) => void;
}

const ready = (alternatives: readonly Alternative[]): AltsState => ({
  status: 'ready',
  options: new Map(alternatives.map(({ change, options }) => [change, options])),
});

/**
 * The version with each change numbered where it is, and other ways to say each one: a click
 * swaps one in, or the writer's own words back, and the version reads that way from then on.
 * Changes are found here and their other ways suggested by the API, matched back by number.
 */
export function AltsView({ original, returned, picks, suggest, onPick }: AltsViewProps) {
  const { t, describeError } = useI18n();
  const segments = useMemo(() => diffText(original, returned), [original, returned]);
  const changes = useMemo(() => changesIn(segments), [segments]);
  const spans = useMemo(() => afterSpans(segments, changes), [segments, changes]);
  const readable = useMemo(
    () => readableChanges(original, returned, segments, changes),
    [changes, original, returned, segments],
  );
  // The version as it reads now, formatted, each change's words marked and numbered where they
  // stand.
  const version = useMemo(() => {
    const stretches: TextStretch[] = [];
    const extras: TextExtra[] = [];
    let text = '';
    for (const [index, { start, end }] of spans.entries()) {
      text += returned.slice(spans[index - 1]?.end ?? 0, start);
      const words = picks.get(index) ?? changes[index]?.after ?? '';
      const at = text.length;
      text += words;
      if (words)
        stretches.push({
          start: at,
          end: text.length,
          draw: (children) => (
            <mark className="alts-slot" data-picked={picks.has(index) || undefined}>
              {children}
            </mark>
          ),
        });
      extras.push({
        at: text.length,
        node: (
          <sup className="change-mark" aria-hidden="true">
            {index + 1}
          </sup>
        ),
      });
      if (index === spans.length - 1) text += returned.slice(end);
    }
    return { text, stretches, extras };
  }, [changes, picks, returned, spans]);
  // Suggested before, as when coming back to this view, the other ways show at once.
  const [state, setState] = useState<AltsState>(() => {
    const known = suggest.known?.({ original, rewrite: returned });
    return known ? ready(known) : { status: 'loading' };
  });
  const [attempt, setAttempt] = useState(0);
  const loading = state.status === 'loading';

  useEffect(() => {
    if (changes.length === 0) return;
    let current = true;
    suggest({
      original,
      rewrite: returned,
      changes: changes.map(({ before, after }) => ({ before, after })),
    }).then(
      (alternatives) => {
        if (current) setState(ready(alternatives));
      },
      (error: unknown) => {
        if (current) setState({ status: 'failed', message: describeError(error) });
      },
    );
    return () => {
      current = false;
    };
  }, [attempt, changes, describeError, original, returned, suggest]);

  if (changes.length === 0) {
    return <p className="explain-empty">{t.explain.unchanged}</p>;
  }

  const pick = (index: number, words: string) => {
    const next = new Map(picks);
    // The version's own words are what it says without a pick.
    if (words === changes[index]?.after) next.delete(index);
    else next.set(index, words);
    onPick(next, replaceSpans(returned, spans, next));
  };

  return (
    <>
      <div className="alts-text rich-text">
        <MarkdownText text={version.text} stretches={version.stretches} extras={version.extras} />
      </div>
      <ol className="explain-list" aria-label={t.card.otherWays} aria-busy={loading}>
        {changes.map((change, index) => {
          const suggested = state.status === 'ready' ? state.options.get(index) : undefined;
          const chosen = picks.get(index) ?? change.after;
          // Its words as they read, without the formatting written around them.
          const { before, after } = readable[index] ?? change;
          // The version's own words first, then the other ways, then the writer's, to go back to.
          const choices = suggested && [
            { words: change.after, label: after, yours: false },
            ...suggested.map((words) => ({ words, label: words, yours: false })),
            ...(change.before ? [{ words: change.before, label: before, yours: true }] : []),
          ];
          return (
            <li
              key={`${String(change.first)}-${String(change.last)}`}
              className="explain-item"
              style={{ '--change': index } as CSSProperties}
            >
              <span className="explain-number" aria-hidden="true">
                {index + 1}
              </span>
              <div className="explain-body">
                <p className="explain-change">
                  {before && <del>{before}</del>}
                  {before && change.after && (
                    <span className="explain-arrow" aria-label={t.explain.became}>
                      →
                    </span>
                  )}
                  {change.after && <ins>{picks.get(index) ?? after}</ins>}
                </p>
                {/* The choices, where a skeleton of them held their place while on their way. */}
                {(loading || choices) && (
                  <div className="explain-slot">
                    <span className="explain-skeleton alts-skeleton" aria-hidden="true">
                      <span className="explain-skeleton-kind" />
                      <span className="explain-skeleton-kind" />
                      <span className="explain-skeleton-kind" />
                    </span>
                    {choices && (
                      <div
                        className="alts-options"
                        role="group"
                        aria-label={t.alts.choices(index + 1)}
                      >
                        {choices.map(({ words, label, yours }) => (
                          <button
                            key={`${String(yours)}:${words}`}
                            type="button"
                            className={cx('alts-option', yours && 'is-yours')}
                            aria-pressed={words === chosen}
                            onClick={() => {
                              pick(index, words);
                            }}
                          >
                            {label}
                            {yours && (
                              <>
                                {' '}
                                <span className="alts-yours">{t.alts.yours}</span>
                              </>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {state.status === 'failed' && (
        <div className="explain-failed" role="alert">
          <p>{state.message}</p>
          <Button
            icon="retry"
            onClick={() => {
              setState({ status: 'loading' });
              setAttempt((count) => count + 1);
            }}
          >
            {t.common.tryAgain}
          </Button>
        </div>
      )}
    </>
  );
}
